import { Controller } from "@hotwired/stimulus";

// Define Stripe types
declare const Stripe: (key: string) => StripeData;

interface StripeError {
  message: string;
  type: string | null;
}

interface StripeSetupResponse {
  error?: StripeError;
}

interface StripeConfirmParams {
  return_url: string;
}

interface StripeElements {
  create(type: string, options?: { mode?: string; layout?: string }): StripeElement;
}

interface StripeElement {
  mount(element: HTMLElement): void;
}

interface StripeData {
  elements(options: { clientSecret: string; loader?: string }): StripeElements;
  confirmSetup(options: {
    elements: StripeElements;
    confirmParams: StripeConfirmParams;
    redirect?: string;
  }): Promise<StripeSetupResponse>;
}

export default class extends Controller {
  static targets = ["address", "payment", "setupIntentId", "metadata", "errorContainer"];

  declare readonly addressTarget: HTMLElement;
  declare readonly paymentTarget: HTMLElement;
  declare readonly setupIntentIdTarget: HTMLInputElement;
  declare readonly metadataTarget: HTMLElement;
  declare readonly errorContainerTarget: HTMLElement;

  declare stripe: StripeData;
  declare elements: StripeElements;
  declare form: HTMLFormElement;

  async connect() {
    const publishableKey = this.metadataTarget.dataset.publishableKey || "";
    const clientSecret = this.metadataTarget.dataset.clientSecret || "";
    this.form = this.element as HTMLFormElement;

    this.stripe = Stripe(publishableKey);
    this.elements = this.stripe.elements({ clientSecret, loader: "always" });

    // Mount payment and address elements
    const paymentElement = this.elements.create("payment", { layout: "tabs" });
    const addressElement = this.elements.create("address", { mode: "billing" });

    paymentElement.mount(this.paymentTarget);
    addressElement.mount(this.addressTarget);

    // Handle form submit in JavaScript
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
  }

  async handleSubmit(event: Event) {
    event.preventDefault();
    this.setProcessing(true);

    const { error } = await this.stripe.confirmSetup({
      elements: this.elements,
      confirmParams: {
        return_url: this.metadataTarget.dataset.redirectUrl || "",
      },
      redirect: "if_required",
    });

    if (error) {
      this.setError(error);
      this.setProcessing(false);
    } else {
      this.setError(null);
      (this.element as HTMLFormElement).submit();
    }
  }

  /**
   * Show error message below the stripe form if something goes wrong
   * during stripe processing
   */
  setError = (error: StripeError | null) => {
    // Validation errors appear inline already so don't need to show again
    if (error && error.type !== "validation_error") {
      this.errorContainerTarget.textContent = error.message;
      this.errorContainerTarget.classList.remove("hidden");
    } else {
      this.errorContainerTarget.classList.add("hidden");
    }
  };

  /**
   * Disable the submit button while processing
   */
  setProcessing = (processing: boolean) => {
    const submitButton = this.element.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = processing;
    }
  };
}
